/**
 * UnifiedTicketsPage — Vue unique de pilotage des tickets OmniHub
 * 
 * Affiche tickets + suggestions dans un seul écran.
 * Filtres par hub, statut, priorité, raison, assignation.
 * Suggestions en haut avec actions rapides (créer/ignorer).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Search, RefreshCw, Loader2, Plus, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, Clock, AlertCircle, Inbox,
  Mail, MessageCircle, Bot, UserPlus, Zap, Eye,
  ArrowUpRight, Filter as FilterIcon, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// ─────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────

const STATUS_CONFIG = {
  open:        { label: "Ouvert",     color: "bg-blue-500/20 text-blue-400 border-blue-500/30",   dot: "bg-blue-400" },
  in_progress: { label: "En cours",   color: "bg-amber-500/20 text-amber-400 border-amber-500/30", dot: "bg-amber-400" },
  waiting:     { label: "En attente", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", dot: "bg-purple-400" },
  done:        { label: "Terminé",    color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400" },
  closed:      { label: "Fermé",      color: "bg-zinc-500/20 text-zinc-500 border-zinc-500/30",   dot: "bg-zinc-500" },
};

const PRIORITY_CONFIG = {
  critical: { label: "Critique", color: "bg-red-500/20 text-red-400 border-red-500/30",    icon: "!" },
  high:     { label: "Haute",    color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: "!" },
  medium:   { label: "Moyenne",  color: "bg-sky-500/20 text-sky-400 border-sky-500/30",     icon: null },
  low:      { label: "Basse",    color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",  icon: null },
};

const SOURCE_ICONS = {
  email: Mail,
  whatsapp: MessageCircle,
  ia_suggestion: Bot,
  manual: UserPlus,
  needs_action: Zap,
  cxo: ArrowUpRight,
};

const HUB_VIEWS = [
  { key: null, label: "Tous" },
  { key: "cxo", label: "CxO" },
  { key: "commercial", label: "Commercial" },
  { key: "operation", label: "Opération" },
  { key: "support", label: "Support" },
];

const STATUS_FILTERS = [
  { key: "open", label: "Ouverts" },
  { key: "in_progress", label: "En cours" },
  { key: "waiting", label: "En attente" },
  { key: "mine", label: "Mes tickets", special: true },
  { key: "unassigned", label: "Non assignés", special: true },
  { key: "done", label: "Terminés" },
  { key: "closed", label: "Fermés" },
  { key: null, label: "Tous" },
];

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <Badge data-testid={`status-badge-${status}`} variant="outline" className={`${cfg.color} text-[11px] font-medium px-2 py-0.5`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1.5 inline-block`} />
      {cfg.label}
    </Badge>
  );
};

const PriorityIndicator = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  if (!cfg.icon) return null;
  return (
    <span data-testid={`priority-${priority}`} className={`inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold ${cfg.color}`}>
      {cfg.icon}
    </span>
  );
};

const SourceIcon = ({ source }) => {
  const Icon = SOURCE_ICONS[source] || Inbox;
  return <Icon className="w-3.5 h-3.5 text-zinc-500" />;
};

// ─────────────────────────────────────────────
// SUGGESTION ROW (bandeau action)
// ─────────────────────────────────────────────

const SuggestionRow = ({ suggestion, onAccept, onReject, loading }) => {
  const SourceIc = SOURCE_ICONS[suggestion.source] || Bot;
  const pcfg = PRIORITY_CONFIG[suggestion.suggested_priority] || PRIORITY_CONFIG.medium;

  return (
    <div data-testid={`suggestion-${suggestion.id}`} className="group relative flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-500/[0.06] to-transparent border-l-[3px] border-amber-400/70">
      {/* Dashed right edge to visually separate from tickets */}
      <div className="absolute top-0 bottom-0 right-0 w-px border-r border-dashed border-amber-500/20" />

      {/* Source icon */}
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/20 flex items-center justify-center">
        <SourceIc className="w-3.5 h-3.5 text-amber-400" />
      </div>

      {/* Label */}
      <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/20">
        Suggestion
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm text-zinc-300 truncate">{suggestion.suggested_title}</p>
          <Badge variant="outline" className={`${pcfg.color} text-[10px] px-1.5 py-0 flex-shrink-0`}>{pcfg.label}</Badge>
        </div>
        {suggestion.suggested_description && (
          <p className="text-xs text-zinc-600 truncate mt-0.5">{suggestion.suggested_description.substring(0, 100)}</p>
        )}
      </div>

      {/* Raison */}
      <div className="hidden md:block text-xs text-zinc-500 w-28 truncate">
        {suggestion.suggested_reason}
      </div>

      {/* Actions — always slightly visible, full on hover */}
      <div className="flex items-center gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
        <Button
          data-testid={`accept-suggestion-${suggestion.id}`}
          size="sm"
          className="h-7 px-3 text-xs bg-emerald-600/80 hover:bg-emerald-600 text-white border-0"
          onClick={() => onAccept(suggestion)}
          disabled={loading}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
          Créer ticket
        </Button>
        <Button
          data-testid={`reject-suggestion-${suggestion.id}`}
          size="sm"
          variant="ghost"
          className="h-7 px-2.5 text-xs text-zinc-500 hover:bg-zinc-500/10 hover:text-zinc-300"
          onClick={() => onReject(suggestion)}
          disabled={loading}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" />
          Ignorer
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// TICKET ROW
// ─────────────────────────────────────────────

const TicketRow = ({ ticket, onSelect, isUrgent }) => {
  const SourceIc = SOURCE_ICONS[ticket.source] || Inbox;

  return (
    <div
      data-testid={`ticket-${ticket.référence}`}
      className={`group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
        isUrgent
          ? "border-l-2 border-red-500/60 bg-red-500/[0.03] hover:bg-red-500/[0.06]"
          : "border-l-2 border-transparent hover:bg-white/[0.02]"
      }`}
      onClick={() => onSelect(ticket)}
    >
      {/* Priority dot */}
      <PriorityIndicator priority={ticket.priority} />

      {/* Source icon */}
      <div className="flex-shrink-0 w-7 h-7 rounded-md bg-zinc-800/50 flex items-center justify-center">
        <SourceIc className="w-3.5 h-3.5 text-zinc-400" />
      </div>

      {/* Ref */}
      <span className="text-xs font-mono text-zinc-500 w-16 flex-shrink-0">{ticket.référence}</span>

      {/* Title + reason */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zinc-200 truncate">{ticket.title}</p>
        <p className="text-xs text-zinc-600 truncate mt-0.5">
          {ticket.reason_label || ticket.reason}
          {ticket.dossier_ref && <span className="ml-2 text-zinc-700">| {ticket.dossier_ref}</span>}
          {ticket.client_name && <span className="ml-2 text-zinc-700">| {ticket.client_name}</span>}
        </p>
      </div>

      {/* Status */}
      <StatusBadge status={ticket.status} />

      {/* Assigned */}
      <div className="hidden lg:block w-24 text-xs text-zinc-500 truncate text-right">
        {ticket.assigned_to_name || ticket.assigned_to}
      </div>

      {/* Arrow */}
      <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors" />
    </div>
  );
};

// ─────────────────────────────────────────────
// TICKET DETAIL DIALOG
// ─────────────────────────────────────────────

const TicketDetailDialog = ({ ticket, open, onClose, onTransition }) => {
  const [note, setNote] = useState("");
  const [transitioning, setTransitioning] = useState(false);

  if (!ticket) return null;

  const handleTransition = async (newStatus) => {
    setTransitioning(true);
    await onTransition(ticket.id, newStatus, note);
    setNote("");
    setTransitioning(false);
  };

  const NEXT_ACTIONS = {
    open: [
      { status: "in_progress", label: "Prendre en charge", color: "bg-amber-500 hover:bg-amber-600" },
      { status: "waiting", label: "Mettre en attente", color: "bg-purple-500 hover:bg-purple-600" },
      { status: "closed", label: "Fermer", color: "bg-zinc-600 hover:bg-zinc-700" },
    ],
    in_progress: [
      { status: "done", label: "Marquer terminé", color: "bg-emerald-500 hover:bg-emerald-600" },
      { status: "waiting", label: "Mettre en attente", color: "bg-purple-500 hover:bg-purple-600" },
      { status: "closed", label: "Fermer", color: "bg-zinc-600 hover:bg-zinc-700" },
    ],
    waiting: [
      { status: "in_progress", label: "Reprendre", color: "bg-amber-500 hover:bg-amber-600" },
      { status: "done", label: "Marquer terminé", color: "bg-emerald-500 hover:bg-emerald-600" },
    ],
    done: [
      { status: "closed", label: "Fermer", color: "bg-zinc-600 hover:bg-zinc-700" },
    ],
    closed: [
      { status: "open", label: "Réouvrir", color: "bg-blue-500 hover:bg-blue-600" },
    ],
  };

  const actions = NEXT_ACTIONS[ticket.status] || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl bg-zinc-950 border-zinc-800" data-testid="ticket-detail-dialog" aria-describedby="ticket-detail-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-zinc-100">
            <span className="font-mono text-sm text-zinc-500">{ticket.référence}</span>
            <StatusBadge status={ticket.status} />
            <Badge variant="outline" className={`${(PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium).color} text-[10px] px-1.5`}>
              {(PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium).label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Title */}
          <h3 className="text-base text-zinc-200 font-medium">{ticket.title}</h3>

          {/* Meta grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <MetaField label="Raison" value={ticket.reason_label || ticket.reason} />
            <MetaField label="Source" value={ticket.source} />
            <MetaField label="Type" value={ticket.type} />
            <MetaField label="Assigné à" value={ticket.assigned_to_name || ticket.assigned_to} />
            {ticket.dossier_ref && <MetaField label="Dossier" value={ticket.dossier_ref} />}
            {ticket.dossier_id && !ticket.dossier_ref && <MetaField label="Dossier" value={ticket.dossier_id} />}
            {ticket.client_name && <MetaField label="Client" value={ticket.client_name} />}
            {ticket.installateur_name && <MetaField label="Installateur" value={ticket.installateur_name} />}
          </div>

          {/* Description */}
          {ticket.description && (
            <div className="p-3 rounded-md bg-zinc-900/50 border border-zinc-800/50">
              <p className="text-xs text-zinc-400 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          )}

          {/* Notes / Timeline */}
          {ticket.notes?.length > 0 && (
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Historique</p>
              {ticket.notes.slice(-5).reverse().map((n, i) => (
                <div key={i} className="flex gap-2 text-xs text-zinc-500 py-1 border-b border-zinc-800/30">
                  <span className="text-zinc-700 w-28 flex-shrink-0">{n.timestamp?.substring(0, 16).replace("T", " ")}</span>
                  <span className="text-zinc-400">{n.content}</span>
                </div>
              ))}
            </div>
          )}

          {/* Transition actions */}
          {actions.length > 0 && (
            <div className="pt-2 border-t border-zinc-800/50">
              <Input
                data-testid="transition-note-input"
                placeholder="Note (optionnel)..."
                value={note}
                onChange={e => setNote(e.target.value)}
                className="mb-3 bg-zinc-900/50 border-zinc-800 text-sm h-9"
              />
              <div className="flex gap-2 flex-wrap">
                {actions.map(a => (
                  <Button
                    key={a.status}
                    data-testid={`transition-${a.status}`}
                    size="sm"
                    className={`${a.color} text-white text-xs h-8`}
                    disabled={transitioning}
                    onClick={() => handleTransition(a.status)}
                  >
                    {transitioning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    {a.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MetaField = ({ label, value }) => (
  <div>
    <span className="text-xs text-zinc-600">{label}</span>
    <p className="text-sm text-zinc-300 truncate">{value}</p>
  </div>
);

// ─────────────────────────────────────────────
// CREATE TICKET DIALOG
// ─────────────────────────────────────────────

const CreateTicketDialog = ({ open, onClose, onCreate, reasons }) => {
  const [form, setForm] = useState({
    title: "", description: "", reason: "", priority: "medium",
    type: "action", dossier_id: "", client_id: "", installateur_id: "",
    assigned_to: "", assigned_to_name: "",
  });
  const [creating, setCreating] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.title || !form.reason || !form.assigned_to) {
      toast.error("Titre, raison et assignation sont obligatoires");
      return;
    }
    if (!form.dossier_id && !form.client_id && !form.installateur_id) {
      toast.error("Au moins un lien obligatoire (dossier, client ou installateur)");
      return;
    }
    setCreating(true);
    await onCreate(form);
    setCreating(false);
    setForm({ title: "", description: "", reason: "", priority: "medium", type: "action", dossier_id: "", client_id: "", installateur_id: "", assigned_to: "", assigned_to_name: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-zinc-950 border-zinc-800" data-testid="create-ticket-dialog" aria-describedby="create-ticket-desc">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">Nouveau ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input data-testid="create-title" placeholder="Titre *" value={form.title} onChange={e => update("title", e.target.value)} className="bg-zinc-900/50 border-zinc-800 text-sm h-9" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select data-testid="create-reason" value={form.reason} onChange={e => update("reason", e.target.value)} className="h-9 rounded-md bg-zinc-900/50 border border-zinc-800 text-sm text-zinc-300 px-2">
              <option value="">Raison *</option>
              {reasons.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <select data-testid="create-priority" value={form.priority} onChange={e => update("priority", e.target.value)} className="h-9 rounded-md bg-zinc-900/50 border border-zinc-800 text-sm text-zinc-300 px-2">
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select data-testid="create-type" value={form.type} onChange={e => update("type", e.target.value)} className="h-9 rounded-md bg-zinc-900/50 border border-zinc-800 text-sm text-zinc-300 px-2">
              <option value="action">Action</option>
              <option value="decision">Décision</option>
              <option value="support">Support</option>
              <option value="incident">Incident</option>
              <option value="info_request">Demande info</option>
              <option value="doc_request">Demande doc</option>
            </select>
            <Input data-testid="create-assigned-to" placeholder="Assigné à (user_id) *" value={form.assigned_to} onChange={e => update("assigned_to", e.target.value)} className="bg-zinc-900/50 border-zinc-800 text-sm h-9" />
          </div>

          <Input data-testid="create-dossier-id" placeholder="Dossier ID (lien obligatoire si pas de client)" value={form.dossier_id} onChange={e => update("dossier_id", e.target.value)} className="bg-zinc-900/50 border-zinc-800 text-sm h-9" />
          <Input data-testid="create-client-id" placeholder="Client ID (optionnel)" value={form.client_id} onChange={e => update("client_id", e.target.value)} className="bg-zinc-900/50 border-zinc-800 text-sm h-9" />
          
          <textarea data-testid="create-description" placeholder="Description..." value={form.description} onChange={e => update("description", e.target.value)} rows={3} className="w-full rounded-md bg-zinc-900/50 border border-zinc-800 text-sm text-zinc-300 px-3 py-2 resize-none" />
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} className="text-zinc-500">Annuler</Button>
          <Button data-testid="create-ticket-submit" onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Créer le ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────
// TRI INTELLIGENT
// ─────────────────────────────────────────────
// Ordre : refus ANAH → bloqués (docs manquants) → critiques → hauts → moyens → bas
// À l'intérieur de chaque groupe : open > in_progress > waiting > done > closed

const PRIORITY_WEIGHT = { critical: 0, high: 1, medium: 2, low: 3 };
const STATUS_WEIGHT = { open: 0, in_progress: 1, waiting: 2, done: 3, closed: 4 };

function sortTickets(tickets) {
  return [...tickets].sort((a, b) => {
    // 0. Actif (open/in_progress/waiting) TOUJOURS avant done/closed
    const aActive = STATUS_WEIGHT[a.status] <= 2 ? 0 : 1;
    const bActive = STATUS_WEIGHT[b.status] <= 2 ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;

    // 1. refus_anah en premier
    const aRefus = a.reason === "refus_anah" ? 0 : 1;
    const bRefus = b.reason === "refus_anah" ? 0 : 1;
    if (aRefus !== bRefus) return aRefus - bRefus;

    // 2. bloqués (doc_manquant, montant_manquant, incoherence_statut)
    const BLOCKED_REASONS = new Set(["doc_manquant", "montant_manquant", "incoherence_statut", "documents_manquants"]);
    const aBlocked = BLOCKED_REASONS.has(a.reason) ? 0 : 1;
    const bBlocked = BLOCKED_REASONS.has(b.reason) ? 0 : 1;
    if (aBlocked !== bBlocked) return aBlocked - bBlocked;

    // 3. priorité
    const aPri = PRIORITY_WEIGHT[a.priority] ?? 2;
    const bPri = PRIORITY_WEIGHT[b.priority] ?? 2;
    if (aPri !== bPri) return aPri - bPri;

    // 4. statut
    const aSt = STATUS_WEIGHT[a.status] ?? 2;
    const bSt = STATUS_WEIGHT[b.status] ?? 2;
    if (aSt !== bSt) return aSt - bSt;

    // 5. plus récent en premier
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function UnifiedTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [hubView, setHubView] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const searchTimeout = useRef(null);

  // ── Fetch ──────────────────────────────────
  const fetchAll = useCallback(async (q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (hubView) params.set("hub_view", hubView);

      // Filtres spéciaux vs status standard
      if (statusFilter === "mine") {
        const userId = localStorage.getItem("user_id") || "";
        if (userId) params.set("assigned_to", userId);
        // Exclure done/closed par défaut pour "Mes tickets"
        params.set("status", "open");
      } else if (statusFilter === "unassigned") {
        params.set("assigned_to", "__unassigned");
      } else if (statusFilter) {
        params.set("status", statusFilter);
      }

      if (priorityFilter) params.set("priority", priorityFilter);
      if (q) params.set("search", q);
      params.set("limit", "200");

      const [ticketsRes, suggestionsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/unified-tickets?${params}`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/unified-tickets/suggestions?status=pending`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/unified-tickets/stats`, { headers: getAuthHeaders() }),
      ]);

      const tData = await ticketsRes.json();
      const sData = await suggestionsRes.json();
      const stData = await statsRes.json();

      // Tri intelligent : refus ANAH > bloqués > critiques > hauts > reste
      let filtered = tData.tickets || [];
      
      // Non-super-admin : exclure les tickets source "cxo" et "groot_agent"
      const userRole = localStorage.getItem("user_role") || "";
      if (userRole !== "super_admin") {
        filtered = filtered.filter(t => t.source !== "cxo" && t.source !== "groot_agent");
      }
      
      const sorted = sortTickets(filtered);

      setTickets(sorted);
      setTotal(tData.total || 0);
      setSuggestions(sData.suggestions || []);
      setStats(stData);
    } catch (err) {
      toast.error("Erreur chargement tickets");
    } finally {
      setLoading(false);
    }
  }, [hubView, statusFilter, priorityFilter, search]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    fetch(`${API}/api/unified-tickets/reasons`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(d => setReasons(d.reasons || [])).catch(() => {});
  }, []);

  const handleSearch = (v) => {
    setSearch(v);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchAll(v), 400);
  };

  // ── Actions ────────────────────────────────
  const handleAcceptSuggestion = async (sug) => {
    setActionLoading(true);
    try {
      const res = await fetch(
        `${API}/api/unified-tickets/suggestions/${sug.id}/accept?assigned_to=user_jjnini_admin&assigned_to_name=JJ%20Nini`,
        { method: "POST", headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (data.success) {
        toast.success(`Ticket ${data.ticket.référence} créé`);
        fetchAll();
      } else {
        toast.error(data.error || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
    setActionLoading(false);
  };

  const handleRejectSuggestion = async (sug) => {
    setActionLoading(true);
    try {
      await fetch(`${API}/api/unified-tickets/suggestions/${sug.id}/reject`, {
        method: "POST", headers: getAuthHeaders(),
      });
      toast.success("Suggestion ignorée");
      fetchAll();
    } catch { toast.error("Erreur réseau"); }
    setActionLoading(false);
  };

  const handleTransition = async (ticketId, newStatus, note) => {
    try {
      const res = await fetch(`${API}/api/unified-tickets/${ticketId}/transition`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ new_status: newStatus, note }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Statut mis à jour`);
        setSelectedTicket(data.ticket);
        fetchAll();
      } else {
        toast.error(data.detail || data.error || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
  };

  const handleCreate = async (form) => {
    try {
      const body = {
        ...form,
        source: "manual",
        dossier_id: form.dossier_id || undefined,
        client_id: form.client_id || undefined,
        installateur_id: form.installateur_id || undefined,
      };
      const res = await fetch(`${API}/api/unified-tickets`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Ticket ${data.ticket.référence} créé`);
        fetchAll();
      } else {
        toast.error(data.detail || "Erreur de création");
      }
    } catch { toast.error("Erreur réseau"); }
  };

  // ── Urgents ────────────────────────────────
  const isUrgent = (t) => t.priority === "critical" || t.reason === "refus_anah";

  // ── Stats bar ──────────────────────────────
  const openCount = stats?.by_status?.open || 0;
  const inProgressCount = stats?.by_status?.in_progress || 0;
  const waitingCount = stats?.by_status?.waiting || 0;
  const sugCount = suggestions.length;

  return (
    <div data-testid="unified-tickets-page" className="h-full flex flex-col">
      {/* HEADER */}
      <div className="flex-shrink-0 px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-zinc-100">Tickets</h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {total} ticket{total !== 1 ? "s" : ""} &middot; {sugCount} suggestion{sugCount !== 1 ? "s" : ""} en attente
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="refresh-tickets" variant="ghost" size="sm" className="h-8 px-2 text-zinc-500" onClick={() => fetchAll()} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="create-ticket-btn" size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => setShowCreate(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau ticket
            </Button>
          </div>
        </div>

        {/* KPI pills */}
        <div className="flex gap-3 mb-4">
          <KpiPill label="Ouverts" value={openCount} color="text-blue-400" bg="bg-blue-500/10" />
          <KpiPill label="En cours" value={inProgressCount} color="text-amber-400" bg="bg-amber-500/10" />
          <KpiPill label="En attente" value={waitingCount} color="text-purple-400" bg="bg-purple-500/10" />
          {sugCount > 0 && <KpiPill label="Suggestions" value={sugCount} color="text-amber-300" bg="bg-amber-400/10" pulse />}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Hub view pills */}
          <div className="flex rounded-md border border-zinc-800 overflow-hidden" data-testid="hub-view-filter">
            {HUB_VIEWS.map(hv => (
              <button
                key={hv.key || "all"}
                data-testid={`hub-filter-${hv.key || "all"}`}
                className={`px-3 py-1.5 text-xs transition-colors ${
                  hubView === hv.key
                    ? "bg-zinc-700/50 text-zinc-100"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30"
                }`}
                onClick={() => setHubView(hv.key)}
              >
                {hv.label}
              </button>
            ))}
          </div>

          {/* Status pills */}
          <div className="flex rounded-md border border-zinc-800 overflow-hidden" data-testid="status-filter">
            {STATUS_FILTERS.map(sf => (
              <button
                key={sf.key || "all"}
                data-testid={`status-filter-${sf.key || "all"}`}
                className={`px-2.5 py-1.5 text-xs transition-colors ${
                  statusFilter === sf.key
                    ? "bg-zinc-700/50 text-zinc-100"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30"
                }`}
                onClick={() => setStatusFilter(sf.key)}
              >
                {sf.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
            <Input
              data-testid="ticket-search"
              placeholder="Rechercher..."
              value={search}
              onChange={e => handleSearch(e.target.value)}
              className="h-8 w-52 pl-8 bg-zinc-900/50 border-zinc-800 text-sm"
            />
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {loading && tickets.length === 0 ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-800/50 overflow-hidden divide-y divide-zinc-800/30">
            {/* Suggestions section — visually separated */}
            {suggestions.length > 0 && (
              <>
                {suggestions.map(sug => (
                  <SuggestionRow
                    key={sug.id}
                    suggestion={sug}
                    onAccept={handleAcceptSuggestion}
                    onReject={handleRejectSuggestion}
                    loading={actionLoading}
                  />
                ))}
                {/* Separator between suggestions and tickets */}
                <div className="flex items-center gap-3 px-4 py-1.5 bg-zinc-900/30">
                  <div className="flex-1 h-px bg-zinc-800" />
                  <span className="text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Tickets</span>
                  <div className="flex-1 h-px bg-zinc-800" />
                </div>
              </>
            )}

            {/* Tickets */}
            {tickets.map(t => (
              <TicketRow
                key={t.id}
                ticket={t}
                onSelect={setSelectedTicket}
                isUrgent={isUrgent(t)}
              />
            ))}

            {tickets.length === 0 && suggestions.length === 0 && (
              <div className="py-12 text-center text-zinc-600 text-sm">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
                Aucun ticket ne correspond aux filtres
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <TicketDetailDialog
        ticket={selectedTicket}
        open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onTransition={handleTransition}
      />

      <CreateTicketDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        reasons={reasons}
      />
    </div>
  );
}

// ─────────────────────────────────────────────
// KPI Pill
// ─────────────────────────────────────────────

const KpiPill = ({ label, value, color, bg, pulse }) => (
  <div data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`} className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${bg}`}>
    {pulse && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
    <span className={`text-lg font-semibold ${color}`}>{value}</span>
    <span className="text-xs text-zinc-500">{label}</span>
  </div>
);
